import {
  Form,
  FormRow,
  Option,
  PopupHint,
  SegmentedButton,
  Select,
} from '@op/opds.opux';
import { useFormValidationAlert } from '@op/luna.yaweb-common-js/FormValidationAlert';
import { Message, useMessageGetter } from '@messageformat/react';
import { IO_CHANGE_INSURANCE } from '../../constants/actionTypes';
import { DEFAULT_LOCALE } from '../../constants/common';
import { useInsuranceOffer } from '../../hooks';
import { buildElementId } from '../../utils';
import ValidationError from '../common/ValidationError/ValidationError';
import { FormInput } from '../common/Input/FormInput';

const HomeQuestions = (insurance) => {
  const { state, validateInsurance } = useInsuranceOffer();
  const { errors, removeErrors } = useFormValidationAlert();
  const newErrors = { add: [], remove: [] };

  const getMessage = useMessageGetter();
  const getDefaultMessage = useMessageGetter([], { locale: DEFAULT_LOCALE });

  const allExceptApartmentBuilding = [
    'House',
    'Terraced house',
    'Detached house',
    'Duplex',
    'Agricultural building',
    'Sauna',
    'Free-time residence',
  ];
  const homes = [
    'House',
    'Apartment building',
    'Terraced house',
    'Detached house',
    'Duplex',
    'Agricultural building',
    'Sauna',
    'Free-time residence',
  ];
  const homesWithArea = [
    'Apartment building',
    'Terraced house',
    'Detached house',
    'Duplex',
  ];
  const homesWithOutArea = [
    'House',
    'Agricultural building',
    'Sauna',
    'Free-time residence',
  ];
  const index = state.selectedInsurances.findIndex(
    (element) => element.id === insurance.id
  );
  const homeDetails = state.selectedInsurances[index].questions;

  const makeInputData = (data, label, answer, errorLabel) => {
    const value = data || '';
    return {
      value,
      dataKey: `${insurance.id}-${label}`,
      question: getMessage(`labelText.${label}`),
      answer: answer ? getMessage(answer) : value,
      defaultQuestion: getDefaultMessage(`labelText.${label}`),
      defaultAnswer: answer ? getDefaultMessage(answer) : value,
      errorLabel: errorLabel
        ? `${insurance.caption} : ${getMessage(
            `labelText.${label}`
          )} - ${getMessage(errorLabel)}`
        : '',
      getMessage,
    };
  };

  const getValue = (answer) => {
    return (answer || { value: '' }).value;
  };

  const getOptionTitle = (event, key) => {
    const { value, selectedIndex } = event.target;
    return value === 'choose'
      ? 'uiText.choose'
      : `option.${key}.${String(selectedIndex).padStart(3, '0')}`;
  };

  const cleanInputsByIds = (arrayList, errs, insId) => {
      return arrayList
        .map((item) => buildElementId(insId, item)) // Create full IDs
        .filter((fullId) => errs.some((err) => err.id === fullId)); // Filter existing ones
    };

  return (
    <Form spacing="default">
      <div>
        <FormRow
          dataKey={`${insurance.id}-homeType`}
          label={<Message id="labelText.homeType" />}
          labelFor={buildElementId(insurance.id, 'HOME_TYPE')}
          required
        >
          <Select
            value={getValue(homeDetails.homeType)}
            aria-describedby='homeTypeError'
            name={`${insurance.id}-homeType`}
            onChange={(e) => {
              newErrors.remove = errors
                .filter((record) => record.id.startsWith(insurance.id))
                .map((record) => record.id);
              removeErrors(...newErrors.remove);
              validateInsurance({
                insurance,
                type: IO_CHANGE_INSURANCE,
                questions: {
                  homeType: makeInputData(
                    e.target.value,
                    'homeType',
                    getOptionTitle(e, 'homeType'),
                    'error.fill.in.missing.information'
                  ),
                },
                questionId: e.target.id,
              });
            }}
            id={buildElementId(insurance.id, 'HOME_TYPE')}
          >
            <option value="choose" key="choose">
              {getMessage('uiText.choose')}
            </option>
            <option value="House" key="House">
              {getMessage('option.homeType.001')}
            </option>
            <option value="Apartment building" key="Apartment building">
              {getMessage('option.homeType.002')}
            </option>
            <option value="Terraced house" key="Terraced house">
              {getMessage('option.homeType.003')}
            </option>
            <option value="Detached house" key="Detached house">
              {getMessage('option.homeType.004')}
            </option>
            <option value="Duplex" key="Duplex">
              {getMessage('option.homeType.005')}
            </option>
            <option value="Agricultural building" key="Agricultural building">
              {getMessage('option.homeType.006')}
            </option>
            <option value="Sauna" key="Sauna">
              {getMessage('option.homeType.007')}
            </option>
            <option value="Free-time residence" key="Free-time residence">
              {getMessage('option.homeType.008')}
            </option>
          </Select>
          <ValidationError
            id='homeTypeError'
            requireVisible={false}
            errorId={buildElementId(insurance.id, 'HOME_TYPE')}
            errorMessageId="error.fill.in.missing.information"
          />
        </FormRow>
      </div>
      {homes.indexOf(getValue(homeDetails.homeType)) > -1 && (
        <div>
          <FormInput
            type="alphaNumeric"
            value={getValue(homeDetails.address)}
            inputId={buildElementId(insurance.id, 'HOME_ADDRESS')}
            onChange={(address , error) => {
              homeDetails.address = makeInputData(
                address,
                'HomeAddress'
              );
              validateInsurance({
                insurance,
                type: IO_CHANGE_INSURANCE,
                questions: homeDetails,
                questionId: `${insurance.id}-HomeAddress`,
                error,
              });                
            }}
            label={getMessage('labelText.HomeAddress')}
            required
          />  
          <FormInput
            type="postal-code-finland"
            value={getValue(homeDetails.postcode)}
            inputId={buildElementId(insurance.id, 'HOME_POSTCODE')}
            onChange={(postcode , error) => {
              homeDetails.postcode = makeInputData(
                postcode,
                'postcode'
              );
              validateInsurance({
                insurance,
                type: IO_CHANGE_INSURANCE,
                questions: homeDetails,
                questionId: `${insurance.id}-postcode`,
                error,
              });                
            }}
            label={getMessage('labelText.postcode')}
            required
          />        
          
          {allExceptApartmentBuilding.indexOf(getValue(homeDetails.homeType)) >
            -1 && (
            <div>
              <FormRow
                dataKey={`${insurance.id}-housingCompany`}
                label={<Message id="labelText.housingCompany" />}
                labelFor={buildElementId(insurance.id, 'HOME_HOUSINGCOMPANY')}
              >
                <SegmentedButton
                  value={getValue(homeDetails.housingcompany)}
                  name={`${insurance.id}-housingcompany`}
                  onChange={(e) => {
                    homeDetails.housingcompany = makeInputData(
                      e.target.value,
                      'housingCompany',
                      `buttonText.${e.target.value.toLowerCase()}`
                    );
                    validateInsurance({
                      insurance,
                      type: IO_CHANGE_INSURANCE,
                      questions: homeDetails,
                      questionId: e.target.id,
                    });
                  }}
                  id={buildElementId(insurance.id, 'HOME_HOUSINGCOMPANY')}
                >
                  <Option value="Yes" key="Yes" required>
                    {<Message id="buttonText.yes" />}
                  </Option>
                  <Option value="No" key="No">
                    {<Message id="buttonText.no" />}
                  </Option>
                </SegmentedButton>
              </FormRow>
            </div>
          )}
          {((homesWithArea.indexOf(getValue(homeDetails.homeType)) > -1 &&
            getValue(homeDetails.housingcompany) === '') ||
            getValue(homeDetails.housingcompany) === 'Yes') && (
            <FormInput
              type="numeric"
              limit={9999}
              amount
              unit={<Message id="unitText.squareMetre" />}
              unitPos="outside"
              value={getValue(homeDetails.floorarea)}
              inputId={buildElementId(insurance.id, 'HOME_FLOORAREA')}
              onChange={(floorarea , error) => {
                homeDetails.floorarea = makeInputData(
                  floorarea,
                  'floorArea'
                );
                validateInsurance({
                  insurance,
                  type: IO_CHANGE_INSURANCE,
                  questions: homeDetails,
                  questionId: `${insurance.id}-floorArea`,
                  error,
                });                
              }}
              label={getMessage('labelText.floorArea')}
              maxLength={4}
              required
            />           
          )}
          {((homesWithOutArea.indexOf(getValue(homeDetails.homeType)) > -1 &&
            getValue(homeDetails.housingcompany) === '') ||
            (getValue(homeDetails.housingcompany) === 'No' &&
              getValue(homeDetails.homeType) !== 'Duplex')) && (
              <FormInput
                type="numeric"
                limit={9999}
                amount
                unit={<Message id="unitText.squareMetre" />}
                unitPos="outside"
                value={getValue(homeDetails.totalfloorarea)}
                inputId={buildElementId(insurance.id, 'HOME_TOTALFLOORAREA')}
                onChange={(totalfloorarea , error) => {
                  homeDetails.totalfloorarea = makeInputData(
                    totalfloorarea,
                    'totalFloorArea'
                  );
                  validateInsurance({
                    insurance,
                    type: IO_CHANGE_INSURANCE,
                    questions: homeDetails,
                    questionId: `${insurance.id}-totalFloorArea`,
                    error,
                  });                
                }}
                label={getMessage('labelText.totalFloorArea')}
                maxLength={4}
                hint={
                  <PopupHint placement="top">
                    <Message id="hintText.totalFloorArea" />
                  </PopupHint>
                }
                required
              />           
          )}
          {getValue(homeDetails.housingcompany) === 'No' &&
            getValue(homeDetails.homeType) === 'Duplex' && (
              <FormInput
                type="numeric"
                limit={9999}
                amount
                unit={<Message id="unitText.squareMetre" />}
                unitPos="outside"
                value={getValue(homeDetails.totalfloorareadetached)}
                inputId={buildElementId(insurance.id, 'HOME_TOTALFLOORAREA_DETACHED')}
                onChange={(totalfloorareadetached , error) => {
                  homeDetails.totalfloorareadetached = makeInputData(
                    totalfloorareadetached,
                    'totalFloorAreaOwnHomeSemi-detached'
                  );
                  validateInsurance({
                    insurance,
                    type: IO_CHANGE_INSURANCE,
                    questions: homeDetails,
                    questionId: `${insurance.id}-totalFloorAreaOwnHomeSemi-detached`,
                    error,
                  });                
                }}
                label={getMessage('labelText.totalFloorAreaOwnHomeSemi-detached')}
                maxLength={4}
                hint={
                  <PopupHint placement="top">
                    <Message id="hintText.bannerTextForTotalOffer" />
                  </PopupHint>
                }
                required
              />             
            )}
            <FormInput
                type="year"
                limit={1700}
                value={getValue(homeDetails.constructionYear)}
                inputId={buildElementId(insurance.id, 'HOME_CONSTRUCTIONYEAR')}
                onChange={(constructionYear , error) => {
                  homeDetails.constructionYear = makeInputData(
                    constructionYear,
                    'constructionYear',
                    '',
                    `${insurance.caption} : ${getMessage(
                    'labelText.constructionYear'
                     )} - ${error?.message}`               
                  );
                  validateInsurance({
                    insurance,
                    type: IO_CHANGE_INSURANCE,
                    questions: homeDetails,
                    questionId: `${insurance.id}-constructionYear`,
                    error,
                  });                
                }}
                label={getMessage('labelText.constructionYear')}
                maxLength={4}
                required
            />         
          <FormRow
            dataKey={`${insurance.id}-purposeOfUse`}
            label={<Message id="labelText.purposeOfUse" />}
            labelFor={buildElementId(insurance.id, 'HOME_PURPOSEOFUSE')}
            required
          >
            <Select
              value={getValue(homeDetails.purposeofuse)}
              aria-describedby='purposeOfUseError'
              name={`${insurance.id}-purposeofuse`}
              onChange={(e) => {
                
                delete homeDetails.monthlyRent;

                const clearInputs = ["HOME_MONTHLY_RENT"]
                newErrors.remove = [...newErrors.remove, ...cleanInputsByIds(clearInputs, errors, insurance.id)]
                removeErrors(...newErrors.remove)
                homeDetails.purposeofuse = makeInputData(
                  e.target.value,
                  'purposeOfUse',
                  getOptionTitle(e, 'purposeOfUse')
                );
                validateInsurance({
                  insurance,
                  type: IO_CHANGE_INSURANCE,
                  questions: homeDetails,
                  questionId: e.target.id,
                });
              }}
              id={buildElementId(insurance.id, 'HOME_PURPOSEOFUSE')}
            >
              <option value="choose" key="choose">
                {getMessage('uiText.choose')}
              </option>
              <option value="1" key="1">
                {getMessage('option.purposeOfUse.001')}
              </option>
              <option value="2" key="2">
                {getMessage('option.purposeOfUse.002')}
              </option>
              <option value="3" key="3">
                {getMessage('option.purposeOfUse.003')}
              </option>
              <option value="4" key="4">
                {getMessage('option.purposeOfUse.004')}
              </option>
            </Select>
            <ValidationError
              id='purposeOfUseError'
              requireVisible={false}
              errorId={buildElementId(insurance.id, 'HOME_PURPOSEOFUSE')}
              errorMessageId="error.fill.in.missing.information"
            />
          </FormRow>
          {getValue(homeDetails.purposeofuse) === '3' && (
            <FormInput
              type="numeric"
              limit={999999}
              value={getValue(homeDetails.monthlyRent)}
              inputId={buildElementId(insurance.id, 'HOME_MONTHLY_RENT')}
              onChange={(monthlyRent, error) => {
                homeDetails.monthlyRent = makeInputData(
                  monthlyRent,
                  'monthlyRent'
                );
                validateInsurance({
                  insurance,
                  type: IO_CHANGE_INSURANCE,
                  questions: homeDetails,
                  questionId: `${insurance.id}-monthlyRent`,
                  error,
                });                
              }}
              label={getMessage('labelText.monthlyRent')}
              amount
              unit={<Message id="unitText.euros" />}
              unitPos="outside"
              required
            />
          )}         
          {homesWithOutArea.indexOf(getValue(homeDetails.homeType)) > -1 && (
            <div>
              <FormRow
                dataKey={`${insurance.id}-waterPipeSystem`}
                label={<Message id="labelText.waterPipeSystem" />}
                labelFor={buildElementId(insurance.id, 'HOME_WATERPIPESYSTEM')}
                hint={
                  <PopupHint placement="top">
                    <Message id="hintText.waterPipeSystem" />
                  </PopupHint>
                }
              >
                <SegmentedButton
                  value={getValue(homeDetails.waterPipeSystem)}
                  aria-describedby='waterPipeSystemError'
                  name={`${insurance.id}-waterPipeSystem`}
                  onChange={(e) => {
                    homeDetails.waterPipeSystem = makeInputData(
                      e.target.value,
                      'waterPipeSystem',
                      `buttonText.${e.target.value.toLowerCase()}`
                    );
                    validateInsurance({
                      insurance,
                      type: IO_CHANGE_INSURANCE,
                      questions: homeDetails,
                      questionId: buildElementId(
                        insurance.id,
                        'HOME_WATERPIPESYSTEM'
                      ),
                    });
                  }}
                  id={buildElementId(insurance.id, 'HOME_WATERPIPESYSTEM')}
                >
                  <Option value="Yes" key="Yes" required>
                    {<Message id="buttonText.yes" />}
                  </Option>
                  <Option value="No" key="No">
                    {<Message id="buttonText.no" />}
                  </Option>
                </SegmentedButton>
                <ValidationError
                  id='waterPipeSystemError'
                  requireVisible={false}
                  errorId={buildElementId(insurance.id, 'HOME_WATERPIPESYSTEM')}
                  errorMessageId="error.fill.in.missing.information"
                />
              </FormRow>

              {getValue(homeDetails.waterPipeSystem) === 'Yes' && (
                <FormInput
                  type="year"
                  limit={1700}
                  value={getValue(homeDetails.YearOfWaterPipeSystem)}
                  inputId={buildElementId(insurance.id, 'HOME_YEAROFWATERPIPESYSTEM')}
                  onChange={(YearOfWaterPipeSystem , error) => {
                    homeDetails.YearOfWaterPipeSystem = makeInputData(
                      YearOfWaterPipeSystem,
                      'YearOfWaterPipeSystem'
                    );
                    validateInsurance({
                      insurance,
                      type: IO_CHANGE_INSURANCE,
                      questions: homeDetails,
                      questionId: `${insurance.id}-YearOfWaterPipeSystem`,
                      error,
                    });                
                  }}
                  label={getMessage('labelText.YearOfWaterPipeSystem')}
                  maxLength={4}
                  hint={
                    <PopupHint placement="top">
                      <Message id="hintText.YearOfWaterPipeSystem" />
                    </PopupHint>
                  }
                  required
                />               
              )}

              <FormRow
                dataKey={`${insurance.id}-heatingSystem`}
                label={<Message id="labelText.heatingSystem" />}
                labelFor={buildElementId(insurance.id, 'HOME_HEATINGSYSTEM')}
                hint={
                  <PopupHint placement="top">
                    <Message id="hintText.heatingSystem" />
                  </PopupHint>
                }
                required
              >
                <Select
                  value={getValue(homeDetails.heatingSystem)}
                  aria-describedby='heatingSystemError'
                  name={`${insurance.id}-heatingSystem`}
                  onChange={(e) => {
                    homeDetails.heatingSystem = makeInputData(
                      e.target.value,
                      'heatingSystem',
                      getOptionTitle(e, 'heatingSystem'),
                      'error.fill.in.missing.information'
                    );
                    validateInsurance({
                      insurance,
                      type: IO_CHANGE_INSURANCE,
                      questions: homeDetails,
                      questionId: e.target.id,
                    });
                  }}
                  id={buildElementId(insurance.id, 'HOME_HEATINGSYSTEM')}
                >
                  <option value="choose" key="choose">
                    {getMessage('uiText.choose')}
                  </option>
                  <option
                    value="Hydronic radiant heat"
                    key="Hydronic radiant heat"
                  >
                    {getMessage('option.heatingSystem.001')}
                  </option>
                  <option
                    value="Other than hydronic radiant heat"
                    key="Other than hydronic radiant heat"
                  >
                    {getMessage('option.heatingSystem.002')}
                  </option>
                  <option value="Unknown" key="Unknown">
                    {getMessage('option.heatingSystem.003')}
                  </option>
                </Select>
                <ValidationError
                  id='heatingSystemError'
                  requireVisible={false}
                  errorId={buildElementId(insurance.id, 'HOME_HEATINGSYSTEM')}
                  errorMessageId="error.fill.in.missing.information"
                />
              </FormRow>
              {getValue(homeDetails.heatingSystem) ===
                'Hydronic radiant heat' && (
                <FormInput
                  type="year"
                  limit={1700}
                  value={getValue(homeDetails.YearOfHeatingSystem)}
                  inputId={buildElementId(insurance.id, 'HOME_YEAROFHEATINGSYSTEM')}
                  onChange={(YearOfHeatingSystem , error) => {
                    homeDetails.YearOfHeatingSystem = makeInputData(
                      YearOfHeatingSystem,
                      'YearOfHeatingSystem',
                      '',
                      `${insurance.caption} : ${getMessage(
                        'labelText.YearOfHeatingSystem'
                      )} - ${error?.message}`                     
                    );
                    validateInsurance({
                      insurance,
                      type: IO_CHANGE_INSURANCE,
                      questions: homeDetails,
                      questionId: `${insurance.id}-YearOfHeatingSystem`,
                      error,
                    });                
                  }}
                  label={getMessage('labelText.YearOfHeatingSystem')}
                  maxLength={4}
                  hint={
                    <PopupHint placement="top">
                      <Message id="hintText.YearOfHeatingSystem" />
                    </PopupHint>
                  }
                  required
                />               
              )}
              <FormRow
                dataKey={`${insurance.id}-electricityType`}
                label={<Message id="labelText.electricityType" />}
                labelFor={buildElementId(insurance.id, 'HOME_ELECTRICITYTYPE')}
                hint={
                  <PopupHint placement="top">
                    <Message id="hintText.electricityType" />
                  </PopupHint>
                }
                required
              >
                <Select
                  value={getValue(homeDetails.electricityType)}
                  aria-describedby='electricityTypeError'
                  name={`${insurance.id}-electricityType`}
                  onChange={(e) => {
                    homeDetails.electricityType = makeInputData(
                      e.target.value,
                      'electricityType',
                      getOptionTitle(e, 'electricity'),
                      'error.fill.in.missing.information'
                    );
                    validateInsurance({
                      insurance,
                      type: IO_CHANGE_INSURANCE,
                      questions: homeDetails,
                      questionId: e.target.id,
                    });
                  }}
                  id={buildElementId(insurance.id, 'HOME_ELECTRICITYTYPE')}
                >
                  <option value="choose" key="choose">
                    {getMessage('uiText.choose')}
                  </option>
                  <option
                    value="Connected to the electricity grid"
                    key="Connected to the electricity grid"
                  >
                    {getMessage('option.electricity.001')}
                  </option>
                  <option
                    value="Own power generation"
                    key="Own power generation"
                  >
                    {getMessage('option.electricity.002')}
                  </option>
                  <option value="No electrification" key="No electrification">
                    {getMessage('option.electricity.003')}
                  </option>
                </Select>
                <ValidationError
                  id='electricityTypeError'
                  requireVisible={false}
                  errorId={buildElementId(insurance.id, 'HOME_ELECTRICITYTYPE')}
                  errorMessageId="error.fill.in.missing.information"
                />
              </FormRow>
            </div>
          )}
        </div>
      )}
    </Form>
  );
};

export default HomeQuestions;
